import { useState, useCallback } from "react";
import { AlertTriangle, Camera, RefreshCw } from "lucide-react";
import CameraLiveness from "./CameraLiveness";
import Turnstile from "./Turnstile";

/**
 * WaitingRoom overlay. Shown when a participant tabs away for 60+ seconds
 * or when a host/cohost manually kicks them to waiting room. To return to
 * the room they must: (1) pass Turnstile verification, (2) pass camera
 * liveness check via MediaPipe. Invisible to other participants but
 * visible to host/cohosts via the staff tray.
 */
export default function WaitingRoom({ reason, onReturn, turnstileSiteKey }) {
  const [step, setStep] = useState("turnstile"); // turnstile | liveness | done
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [livenessError, setLivenessError] = useState(null);

  const handleTurnstileSuccess = useCallback((token) => {
    setTurnstileToken(token);
    setStep("liveness");
  }, []);

  const handleLivenessPass = useCallback((metrics) => {
    setStep("done");
    onReturn({ turnstileToken, livenessMetrics: metrics });
  }, [onReturn, turnstileToken]);

  const handleLivenessFail = useCallback((failReason) => {
    setLivenessError(failReason);
  }, []);

  const retryLiveness = () => {
    setLivenessError(null);
    setStep("liveness");
  };

  const reasonText = {
    tab_away: "You navigated away from the room.",
    host_action: "A host or cohost moved you to the waiting room.",
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#050508]/95 backdrop-blur-md flex items-center justify-center"
         data-testid="waiting-room-overlay">
      <div className="max-w-md w-full mx-4 p-8 rounded-2xl bg-[#0e0e14] border border-white/10 shadow-2xl text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-amber-400" />
        </div>

        <h2 className="text-xl font-bold text-white mb-2">Waiting Room</h2>
        <p className="text-white/60 text-sm mb-6">
          {reasonText[reason] || "You've been moved to the waiting room."}{" "}
          To rejoin, please verify your identity below.
        </p>

        {step === "turnstile" && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-xs text-white/40 uppercase tracking-widest">Step 1: Human verification</p>
            <Turnstile
              siteKey={turnstileSiteKey || "0x4AAAAAABcMcHl7qMRKFQHl"}
              onSuccess={handleTurnstileSuccess}
            />
          </div>
        )}

        {step === "liveness" && !livenessError && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-xs text-white/40 uppercase tracking-widest">Step 2: Camera check</p>
            <CameraLiveness
              thresholds={{ min_frames: 8, min_face_hit_rate: 0.6, min_motion_score: 1.5, sample_duration_ms: 3000 }}
              onPass={handleLivenessPass}
              onFail={handleLivenessFail}
            />
          </div>
        )}

        {step === "liveness" && livenessError && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-red-400 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Verification failed: {livenessError}
            </p>
            <button
              onClick={retryLiveness}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Try again
            </button>
          </div>
        )}

        {step === "done" && (
          <p className="text-emerald-400 text-sm">Verified! Returning to room...</p>
        )}
      </div>
    </div>
  );
}
